import React from 'react';
import { withStyles, WithStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  RadioButtonUnchecked as RadiusIcon,
} from '@mui/icons-material';

const styles = (theme: Theme) => ({
  root: {
    padding: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  serviceAreaCard: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  coordinateRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  coordinateField: {
    flex: 1,
  },
  radiusField: {
    minWidth: 200,
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  helpText: {
    marginBottom: theme.spacing(2),
    fontStyle: 'italic',
  },
});

interface ExternalServiceArea {
  name?: string;
  geoLocation: {
    latitude: number;
    longitude: number;
  };
  radiusMeters: number;
}

interface InternalServiceArea {
  id: string;
  name: string;
  latitude: string | number;
  longitude: string | number;
  radiusMeters: string | number;
}

interface VendorServiceAreaManagerOwnProps {
  serviceAreas?: ExternalServiceArea[];
  onChange?: (serviceAreas: ExternalServiceArea[]) => void;
}

interface VendorServiceAreaManagerProps
  extends VendorServiceAreaManagerOwnProps,
    WithStyles<typeof styles> {}

interface VendorServiceAreaManagerState {
  serviceAreas: InternalServiceArea[];
}

class VendorServiceAreaManager extends React.Component<
  VendorServiceAreaManagerProps,
  VendorServiceAreaManagerState
> {
  constructor(props: VendorServiceAreaManagerProps) {
    super(props);
    const serviceAreas = Array.isArray(props.serviceAreas) ? props.serviceAreas : [];
    this.state = {
      serviceAreas: this.normalizeServiceAreas(serviceAreas),
    };
  }

  override componentDidUpdate(prevProps: VendorServiceAreaManagerProps): void {
    if (prevProps.serviceAreas !== this.props.serviceAreas) {
      const serviceAreas = Array.isArray(this.props.serviceAreas) ? this.props.serviceAreas : [];
      this.setState({
        serviceAreas: this.normalizeServiceAreas(serviceAreas),
      });
    }
  }

  normalizeServiceAreas = (serviceAreas: ExternalServiceArea[]): InternalServiceArea[] => {
    return serviceAreas.map((area, index) => ({
      id: `service_area_${index}`,
      name: area.name || '',
      latitude: area.geoLocation?.latitude || '',
      longitude: area.geoLocation?.longitude || '',
      radiusMeters: area.radiusMeters || 5000,
    }));
  };

  denormalizeServiceAreas = (serviceAreas: InternalServiceArea[]): ExternalServiceArea[] => {
    return serviceAreas
      .filter(
        (area) =>
          area.latitude !== '' && area.longitude !== '' && Number(area.radiusMeters) > 0,
      )
      .map((area) => ({
        name: area.name || `Service Area ${area.latitude}, ${area.longitude}`,
        geoLocation: {
          latitude: parseFloat(String(area.latitude)),
          longitude: parseFloat(String(area.longitude)),
        },
        radiusMeters: parseInt(String(area.radiusMeters), 10),
      }));
  };

  handleServiceAreaChange = (areaId: string, field: string, value: string): void => {
    this.setState((prevState) => {
      const newServiceAreas = prevState.serviceAreas.map((area) => {
        if (area.id === areaId) {
          return { ...area, [field]: value } as InternalServiceArea;
        }
        return area;
      });

      const denormalizedAreas = this.denormalizeServiceAreas(newServiceAreas);
      if (this.props.onChange) {
        this.props.onChange(denormalizedAreas);
      }

      return { serviceAreas: newServiceAreas };
    });
  };

  handleAddServiceArea = (): void => {
    const newArea: InternalServiceArea = {
      id: `service_area_${Date.now()}`,
      name: '',
      latitude: '',
      longitude: '',
      radiusMeters: 5000,
    };

    this.setState((prevState) => ({
      serviceAreas: [...prevState.serviceAreas, newArea],
    }));
  };

  handleRemoveServiceArea = (areaId: string): void => {
    this.setState((prevState) => {
      const newServiceAreas = prevState.serviceAreas.filter((area) => area.id !== areaId);

      const denormalizedAreas = this.denormalizeServiceAreas(newServiceAreas);
      if (this.props.onChange) {
        this.props.onChange(denormalizedAreas);
      }

      return { serviceAreas: newServiceAreas };
    });
  };

  validateCoordinate = (type: 'latitude' | 'longitude', value: string | number): boolean => {
    if (value === '') return true;

    const num = parseFloat(String(value));
    if (isNaN(num)) return false;

    if (type === 'latitude') {
      return num >= -90 && num <= 90;
    } else if (type === 'longitude') {
      return num >= -180 && num <= 180;
    }

    return true;
  };

  validateRadius = (value: string | number): boolean => {
    if (value === '') return false;
    const num = parseInt(String(value), 10);
    return !isNaN(num) && num > 0;
  };

  renderServiceAreaCard = (area: InternalServiceArea, index: number): React.JSX.Element => {
    const { classes } = this.props;

    const latValid = this.validateCoordinate('latitude', area.latitude);
    const lngValid = this.validateCoordinate('longitude', area.longitude);
    const radiusValid = this.validateRadius(area.radiusMeters);
    const isComplete =
      area.latitude !== '' && area.longitude !== '' && Number(area.radiusMeters) > 0;

    return (
      <Card key={area.id} className={classes.serviceAreaCard} variant="outlined">
        <CardContent>
          <div className={classes.cardHeader}>
            <Typography variant="h6" color="primary">
              <LocationIcon sx={{ marginRight: 1, verticalAlign: 'middle' }} />
              Service Area {index + 1}
              {isComplete && <span style={{ color: 'green', marginLeft: 8 }}>✓</span>}
            </Typography>
            <IconButton
              color="error"
              onClick={() => this.handleRemoveServiceArea(area.id)}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Area Name (Optional)"
                value={area.name}
                onChange={(e) => this.handleServiceAreaChange(area.id, 'name', e.target.value)}
                placeholder="e.g., Downtown, North County, etc."
                helperText="Give this service area a descriptive name"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Latitude"
                value={area.latitude}
                onChange={(e) =>
                  this.handleServiceAreaChange(area.id, 'latitude', e.target.value)
                }
                placeholder="37.7749"
                error={!latValid}
                helperText={latValid ? 'Decimal degrees (-90 to 90)' : 'Invalid latitude'}
                type="number"
                inputProps={{ step: 'any', min: -90, max: 90 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Longitude"
                value={area.longitude}
                onChange={(e) =>
                  this.handleServiceAreaChange(area.id, 'longitude', e.target.value)
                }
                placeholder="-122.4194"
                error={!lngValid}
                helperText={lngValid ? 'Decimal degrees (-180 to 180)' : 'Invalid longitude'}
                type="number"
                inputProps={{ step: 'any', min: -180, max: 180 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Service Radius (meters)"
                value={area.radiusMeters}
                onChange={(e) =>
                  this.handleServiceAreaChange(area.id, 'radiusMeters', e.target.value)
                }
                error={!radiusValid}
                helperText={
                  radiusValid
                    ? `${(Number(area.radiusMeters) / 1000).toFixed(1)} km radius`
                    : 'Must be greater than 0'
                }
                type="number"
                inputProps={{ min: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RadiusIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                💡 Tip: Use online tools like Google Maps to find latitude/longitude coordinates
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  override render(): React.JSX.Element {
    const { classes } = this.props;
    const { serviceAreas } = this.state;
    const validAreas = serviceAreas.filter(
      (area) => area.latitude !== '' && area.longitude !== '' && Number(area.radiusMeters) > 0,
    );

    return (
      <div className={classes.root}>
        <Typography variant="h6" className={classes.header}>
          <LocationIcon />
          Service Areas
        </Typography>

        <Typography variant="body2" color="textSecondary" className={classes.helpText}>
          Define the geographic areas this vendor serves. Each area is specified by a center point
          (latitude/longitude) and a radius in meters. If no service areas are specified, the vendor
          is assumed to serve areas near their business address.
        </Typography>

        {serviceAreas.length === 0 ? (
          <Box className={classes.emptyState}>
            <LocationIcon fontSize="large" color="inherit" />
            <Typography variant="body1" gutterBottom>
              No service areas defined
            </Typography>
            <Typography variant="body2">
              Add service areas to specify where this vendor provides services
            </Typography>
          </Box>
        ) : (
          serviceAreas.map((area, index) => this.renderServiceAreaCard(area, index))
        )}

        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={this.handleAddServiceArea}
          className={classes.addButton}
        >
          Add Service Area
        </Button>

        {serviceAreas.length > 0 && (
          <>
            <Divider sx={{ marginY: 2 }} />
            <Typography variant="caption" color="textSecondary">
              {validAreas.length} of {serviceAreas.length} service area(s) will be saved
            </Typography>
            {validAreas.length > 0 && (
              <Box sx={{ marginTop: 1 }}>
                {validAreas.map((area, index) => (
                  <Typography
                    key={area.id}
                    variant="caption"
                    display="block"
                    color="textSecondary"
                  >
                    • {area.name || `Area ${index + 1}`}: {area.latitude}, {area.longitude} (
                    {(Number(area.radiusMeters) / 1000).toFixed(1)}km radius)
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(VendorServiceAreaManager);
